// React & Core Utilities
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

// MUI Components
import Card from "@mui/material/Card";
import { Checkbox } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

// Soft UI Dashboard - Layout & Components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Table from "examples/Tables/Table";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftPagination from "components/SoftPagination";
import DeleteProject from "components/SoftDialog/DeleteProject";
import CircularLoader from "components/SoftDialog/Loader";

// Store / State Management
import useStore from "store/hook";
import UserRow, { columns, Inspectorcolumns } from "store/action/Projects";

// Assets (Images / SVGs)
import { ReactComponent as EmptyFolderIcon } from "assets/images/small-logos/icon-emptyFolder.svg";
import headerBg from "assets/images/curved-images/Header.png";

// Utilities & API
import { toast } from "react-toastify";
import api from "store/api/api";
import db from "../../dexieDB";

// Projects Component
// This component displays a list of projects with search and pagination functionality
// It uses a dashboard layout with a navbar, and includes a delete project dialog
// The component fetches project data from an API and caches it in a Dexie database for offline access
// It also handles user interactions such as searching for projects and toggling deleted projects visibility
function Projects() {
  
  // Hooks and State Management
  const navigate = useNavigate();
  const [page, setPage] = React.useState(1);
  const { setDialogue, store } = useStore();
  const [isLoading, setIsLoading] = useState(false);
  const [searchData, setSearchData] = React.useState("");
  const [rows, setRows] = useState([]);
  const [projectData, setProjectData] = useState([]);
  const [deletedProjects, setDeletedProjects] = useState(false);

  // Local Storage Data (replace with Dexie or other state management if needed)
  const loginData = JSON.parse(localStorage.getItem("loginData"));
  let lastFailedRequest  = false ;

  const handleSearch = () => {
    getProjects("search", searchData);
  };

  // Define Label if needed
  const Label = { inputProps: { "aria-label": "Checkbox label" } };

  // Dialogs and Modals
  const { projectId } = store?.app?.dialogs?.deleteProject || {};
  // Check if the dialog is open
  const { success } = store?.app?.dialogs?.isSuccess || {};

  // Effect to check login status and fetch projects
  React.useEffect(() => {
    const loginData = JSON.parse(localStorage.getItem("loginData"));
    if (!loginData) {
      navigate("/authentication/sign-in");
    } else if (searchData === "" && deletedProjects == false) {
      getProjects();
    } else {
      getProjects("all", searchData, deletedProjects);
    }
  }, [navigate, searchData, page, success, deletedProjects]);

  // Function to fetch projects based on type and search data
  // type can be "all", "search", or "deleted"
  // searchData is the search term for filtering projects
  // deletedProjects is a boolean to include deleted projects
  // page is the current page number for pagination
  // limit is the number of projects per page (default is 50)
  // The function updates the state with the fetched projects and handles errors
  // It also sets the loading state to true while fetching data and false after completion
  const getProjects = async (type = "all", searchData, deletedProjects) => {
    // Determine type based on searchData
    type = searchData?.length > 0 ? "search" : "all";

    try {
      setIsLoading(true);

      // Fetch projects from API
      const response = await api.getProjectsList({
        type,
        page,
        searchData: searchData,
        is_deleted_needed: deletedProjects,
        limit: 50
      });

      if (response.status === 1) {
        // Save to Dexie DB (browser-based database)
        await db.projects.clear();
        await db.projects.bulkAdd(response.data.projects);

        // Update the state with the fetched projects
        setRows(
          response.data.projects.map((project) => UserRow(project, setDialogue))
        );
        setProjectData(response.data);
      }
    } catch (error) {
      lastFailedRequest = true;
      console.error("Error fetching projects:", error);

      // Try loading from cached data if available
      const cached = await db.projects.toArray();
      if (cached.length > 0) {
        setRows(cached.map((project) => UserRow(project, setDialogue)));
        setProjectData({ totalProjects: cached.length });
        toast.warn("You are viewing cached data.");
      } else {
        toast.error("Oops! Slow network or server issue. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Event listener for online status to retry fetching projects
  // If the last request failed and the user is back online, it retries fetching projects
  // This is useful for handling network issues gracefully
  window.addEventListener("online", () => {
    if (lastFailedRequest) {
      getProjects();
      lastFailedRequest = false;
    }
  });

  // Function to handle checkbox change for deleted projects
  // It resets the page to 1 when the checkbox is toggled
  // This ensures that the user sees the first page of results when toggling the filter
  const handleChange = () => {
    setPage(1);
  };

  // Render the Projects component
  // It includes the dashboard layout, navbar, delete project dialog, and the main content
  // The main content consists of a card with project statistics, a search input, and a
  // table displaying the projects
  // It also includes pagination for navigating through the project list
  // The component uses various Soft UI components for styling and layout
  // The table displays different columns based on the user type (inspector or regular user)
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <DeleteProject />
      <SoftBox pt={1}>
        <SoftBox mb={3}>
          <Card>
            <SoftBox
              sx={{
                backgroundColor: "primary.main",
                backgroundImage: `url(${headerBg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                position: "sticky",
                top: 0,
                zIndex: 2,
                overflowX: "auto",
                width: "100%",
                whiteSpace: "nowrap",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              border="2px solid #ccc"
              gap={2}
              px={3}
              py={2}
            >
              <SoftTypography
                color="white"
                variant="h6"
                fontSize="20px"
                fontWeight="light"
              >
                Total Projects -{" "}
                <SoftTypography component="span" variant="h6" fontSize="20px">
                  {projectData.totalProjects || 0}
                </SoftTypography>
              </SoftTypography>
              <SoftBox display="flex" gap={2}>
                {loginData?.account_type !== "inspector" && (
                  <SoftBox
                    onClick={() => navigate("/projects/add-project")}
                    sx={{
                      cursor: "pointer",
                      border: "2px solid white",
                      borderRadius: "6px",
                      padding: "0px 42px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <SoftTypography variant="h6" color="white">
                      Add Projects
                    </SoftTypography>
                  </SoftBox>
                )}

                <SoftBox
                  sx={{
                    width: "280px",
                    borderRadius: "6px",
                  }}
                >
                  <SoftInput
                    value={searchData}
                    onChange={(e) => setSearchData(e.target.value)}
                    placeholder="Search..."
                    bgcolor="white"
                    icon={{
                      component: (
                        <SearchIcon
                          onClick={searchData.trim() ? handleSearch : undefined}
                          sx={{
                            cursor: searchData.trim() ? "pointer" : "default",
                          }}
                        />
                      ),
                      direction: "left",
                    }}
                    sx={{
                      padding: "10px 12px 10px 4px",
                      "& .MuiInputBase-input": {
                        fontSize: "16px",
                        fontWeight: "regular",
                      },
                    }}
                  />
                </SoftBox>

                <SoftBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "0px 11px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    backgroundColor: "white.main",
                  }}
                >
                  <SoftBox position="relative" mr={0.5}>
                    <Checkbox
                      {...Label}
                      checked={deletedProjects}
                      onClick={() => {
                        handleChange();
                        setDeletedProjects((prev) => !prev);
                      }}
                    />
                  </SoftBox>
                  <SoftTypography
                    fontSize="16px"
                    sx={{
                      color: "lightBlack.main",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    Deleted Projects
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox
              sx={{
                position: "relative",
                "& .MuiTableContainer-root": {
                  maxHeight: rows.length > 0 ? "calc(100vh - 245px)" : "auto",
                  overflow: rows.length > 0 ? "auto" : "visible",
                },
                "& .MuiTableHead-root": {
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  backgroundColor: "white",
                },
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {isLoading ? (
                <SoftBox
                  display="flex"
                  height="calc(100vh - 180px)"
                  justifyContent="center"
                  alignItems="center"
                  p={3}
                >
                  <SoftTypography variant="subtitle1" color="textSecondary">
                    <CircularLoader type="large" />
                  </SoftTypography>
                </SoftBox>
              ) : rows.length > 0 ? (
                <Table
                  columns={
                    loginData?.account_type !== "inspector"
                      ? columns
                      : Inspectorcolumns
                  }
                  rows={rows}
                />
              ) : (
                <SoftBox
                  sx={{
                    height: "calc(100vh - 180px)",
                    overflowY: "auto",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "start",
                  }}
                >
                  <SoftBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    gap={8}
                    width="100%"
                  >
                    <SoftBox
                      display="flex"
                      justifyContent="space-between"
                      width="100%"
                      backgroundColor="shadow.main"
                    >
                      {columns.map(({ name, align, width }, index) => (
                        <SoftBox
                          key={index}
                          width={width}
                          align={align}
                          fontWeight="bold"
                          fontSize="18px"
                          py="14px"
                          px="24px"
                        >
                          {name}
                        </SoftBox>
                      ))}
                    </SoftBox>

                    <SoftBox
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <SoftTypography
                        fontSize="90px"
                        fontWeight="light"
                        height="110px"
                        color="fade"
                      >
                        0
                      </SoftTypography>
                      <SoftTypography
                        fontSize="40px"
                        fontWeight="medium"
                        color="fade"
                        pb={2}
                      >
                        PROJECTS
                      </SoftTypography>
                      <EmptyFolderIcon style={{ opacity: 0.8 }} />
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
              )}
            </SoftBox>
            {rows.length > 0 && !isLoading && (
              <SoftBox
                sx={{
                  backgroundColor: "#ffffff",
                  position: "sticky",
                  bottom: 0,
                  zIndex: 2,
                }}
              >
                <SoftPagination
                  Totalpages={projectData.totalPages}
                  currentPage={projectData.currentPage}
                  changePage={setPage}
                />
              </SoftBox>
            )}
          </Card>
        </SoftBox>
      </SoftBox>
    </DashboardLayout>
  );
}

export default Projects;
